import React, { useCallback, useEffect, useState } from 'react';
import { Api } from '../../api';
import './ListPersons.scss';
import { DashboardLayout } from '../../layouts/dashboard-layout/DashboardLayout';
import { User } from '../../models/User';
import { ESortDirection, ETableColumnAlign, ResponsiveTable, ResponsiveTableColumn } from '../../features/responsive-table/ResponsiveTable';
import { Avatar } from '../../features/avatar/Avatar';
import Button from 'react-bootstrap/esm/Button';
import { Search } from '../../features/search/Search';
import Spinner from 'react-bootstrap/esm/Spinner';
import queryString from 'querystring';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';
import { ERouterPath } from '../../features/router/ERouterPath';
import { DateHelper } from '../../helpers/DateHelper';

interface IPersonsTableData {
  avatar: JSX.Element;
  name: string;
  createdAt: string;
  lastActivity: string;
  role: string;
  buttons: JSX.Element;
}

export function ListPersons(): JSX.Element {
  const [persons, setPersons] = useState<User[]>([]);
  const [data, setData] = useState<IPersonsTableData[]>([]);
  const [searchKeys, setSearchKeys] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const location = useLocation();
  const history = useHistory();

  const parameters = queryString.parse(location.search.replace(/^\?/, ''));
  const addToDossier = +(parameters['add-to-dossier'] || '') || undefined;

  const sort = (indexA: number, indexB: number, direction: ESortDirection, objectKey: keyof IPersonsTableData): number => {
    const a = persons[indexA];
    const b = persons[indexB];

    const sortDesc = direction === ESortDirection.DESCENDING;
    switch (objectKey) {
      case 'createdAt':
        const aCreatedAt = a.createdAt || 0;
        const bCreatedAt = b.createdAt || 0;
        return sortDesc ? bCreatedAt.valueOf() - aCreatedAt.valueOf() : aCreatedAt.valueOf() - bCreatedAt.valueOf();
      case 'lastActivity':
        const aLastActivity = a.lastActivity || 0;
        const bLastActivity = b.lastActivity || 0;
        return sortDesc ? bLastActivity.valueOf() - aLastActivity.valueOf() : aLastActivity.valueOf() - bLastActivity.valueOf();
      default:
        return 0;
    }
  };

  const columns: ResponsiveTableColumn<IPersonsTableData>[] = [
    { title: '', objectKey: 'avatar', disableSort: true, verticalAlign: ETableColumnAlign.CENTER },
    { title: 'Naam van persoon', objectKey: 'name', verticalAlign: ETableColumnAlign.CENTER },
    { title: 'Aangemaakt op', objectKey: 'createdAt', verticalAlign: ETableColumnAlign.CENTER, customSortFunction: sort },
    { title: 'Laatst actief', objectKey: 'lastActivity', verticalAlign: ETableColumnAlign.CENTER, customSortFunction: sort },
    { title: 'Rol', objectKey: 'role', verticalAlign: ETableColumnAlign.CENTER },
    { title: '', objectKey: 'buttons', disableSort: true, verticalAlign: ETableColumnAlign.CENTER },
  ];

  useEffect(() => {
    Api.listPersons()
      .then(list => setPersons(list))
      .catch((error) => console.error('Loading persons failed:', error))
      .finally(() => setLoading(false));
  }, []);

  const filter = useCallback((person: User): boolean => {
    if (!searchKeys) {
      return true;
    }
    const sanitizedKeys = searchKeys.trim().toLowerCase();
    if (!sanitizedKeys) {
      return true;
    } else {
      return person.name.toLowerCase().includes(sanitizedKeys);
    }
  }, [searchKeys]);

  const handleButtonClick = useCallback((person: User): void => {
    if (addToDossier) {
      history.push(`${ERouterPath.addPersonToDossier}?add-to-dossier=${addToDossier}&add-person=${person.id}`);
    } else {
      history.push(ERouterPath.updatePerson.replace(':personId', `${person.id}`));
    }
  }, [addToDossier, history]);

  const getButtonLabel = useCallback((): string => {
    if (addToDossier) {
      return 'Voeg toe aan dossier';
    } else {
      return 'Bewerken';
    }
  }, [addToDossier]);

  const getLastActivityText = (person: User): string => {
    if (person.accountId) {
      return person.lastActivity ? DateHelper.toDateTimeString(person.lastActivity) : '-';
    } else {
      return 'Geen toegang';
    }
  };

  useEffect(() => {
    const newData: IPersonsTableData[] = persons.filter(filter).map(person => ({
      avatar: <Avatar className="list-persons__avatar" user={person} />,
      name: person.name,
      createdAt: person.createdAt ? DateHelper.toDateTimeString(person.createdAt) : '',
      lastActivity: getLastActivityText(person),
      role: person.getRoleText({ capitalize: true }),
      buttons: <>
        <Button variant="primary" className="responsive-table__button" onClick={() => handleButtonClick(person)}>{getButtonLabel()}</Button>
      </>
    }));
    setData(newData);
  }, [persons, handleButtonClick, getButtonLabel, filter]);

  const renderContent = () => <>
    <div className="sort-search-container list-persons__search-container">
      <Search onSearch={value => setSearchKeys(value)} inverted={true} placeholder="Zoek op naam van persoon"/>
    </div>
    <ResponsiveTable<IPersonsTableData> columns={columns} data={data} sortByDefault={columns[1]} whiteRows={true} />
  </>;

  return (
    <DashboardLayout backButton={false} noTabs={true}>
      { loading ? <Spinner animation="border" role="status" /> : renderContent() }
    </DashboardLayout>
  );
}
